import { z } from "zod";

/**
 * Sayfa sistemi (P-16, küçük sürüm K-141): hukuki, yardım ve güven sayfaları gibi serbest sayfalar.
 * İçerik dil başına blok listesi (`cms_page_translations.blocks`). HTML bloğu yok: yalnızca bu güvenli bloklar.
 * Metinler düz yazıdır; `[yazı](adres)` biçimi bağlantı olur (yalnızca https:// veya / ile başlayan adres).
 */

/** Yalnızca https:// ya da site içi /yol; `//` ve javascript: kabul edilmez */
export const SafeHref = z
  .string()
  .trim()
  .max(500)
  .regex(/^(https:\/\/[^\s<>"]+|\/(?!\/)[^\s<>"]*)$/);

const Text = (max: number) => z.string().trim().min(1).max(max);

export const CmsBlock = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("heading"),
    level: z.union([z.literal(2), z.literal(3)]),
    text: Text(200),
  }),
  z.object({ type: z.literal("paragraph"), text: Text(5000) }),
  z.object({
    type: z.literal("list"),
    ordered: z.boolean(),
    items: z.array(Text(1000)).min(1).max(50),
  }),
  z.object({
    type: z.literal("button"),
    label: Text(60),
    href: SafeHref,
    style: z.enum(["primary", "secondary"]),
  }),
  z.object({
    type: z.literal("faq"),
    items: z
      .array(z.object({ q: Text(300), a: Text(3000) }))
      .min(1)
      .max(30),
  }),
  z.object({
    type: z.literal("callout"),
    tone: z.enum(["info", "warning"]),
    title: z.string().trim().max(120).optional(),
    text: Text(2000),
  }),
  z.object({ type: z.literal("divider") }),
]);
export type CmsBlock = z.infer<typeof CmsBlock>;

export const CMS_KINDS = ["page", "help_article", "blog_post"] as const;
export type CmsKind = (typeof CMS_KINDS)[number];

export const CmsSlug = z
  .string()
  .trim()
  .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/)
  .min(2)
  .max(80);

/** PUT /v1/admin/pages/:id/:locale */
export const CmsTranslationBody = z.object({
  title: Text(200),
  slug: CmsSlug,
  excerpt: z.string().trim().max(400).nullable().default(null),
  blocks: z.array(CmsBlock).max(200),
  seoTitle: z.string().trim().max(160).nullable().default(null),
  seoDescription: z.string().trim().max(300).nullable().default(null),
  noindex: z.boolean().default(false),
});
export type CmsTranslationBody = z.infer<typeof CmsTranslationBody>;

/** POST /v1/admin/pages */
export const CmsCreateBody = z.object({
  kind: z.enum(CMS_KINDS),
  locale: z.string().min(2).max(10),
  title: Text(200),
  slug: CmsSlug,
});
export type CmsCreateBody = z.infer<typeof CmsCreateBody>;

export interface AdminPageSummary {
  id: string;
  kind: CmsKind;
  status: "draft" | "in_review" | "scheduled" | "published" | "archived";
  /** dil → başlık ve adres */
  translations: { locale: string; title: string; slug: string }[];
  publishedAt: string | null;
  updatedAt: string;
}

export interface AdminPage extends AdminPageSummary {
  /** pazarın dilleri */
  locales: string[];
  content: Record<string, CmsTranslationBody & { updatedAt: string }>;
}

/** GET /v1/markets/:market/pages/:slug?locale= */
export interface PublicPage {
  id: string;
  kind: CmsKind;
  locale: string;
  title: string;
  slug: string;
  excerpt: string | null;
  blocks: CmsBlock[];
  seoTitle: string | null;
  seoDescription: string | null;
  noindex: boolean;
  publishedAt: string;
  updatedAt: string;
}

/** Düz metindeki [yazı](adres) bağlantılarını parçalar. Geçersiz adres düz yazı kalır. */
export type InlinePart = { text: string; href?: string };
export function parseInline(input: string): InlinePart[] {
  const parts: InlinePart[] = [];
  const re = /\[([^\]\n]{1,200})\]\(([^)\s]{1,500})\)/g;
  let last = 0;
  for (const m of input.matchAll(re)) {
    const idx = m.index ?? 0;
    if (!SafeHref.safeParse(m[2]).success) continue;
    if (idx > last) parts.push({ text: input.slice(last, idx) });
    parts.push({ text: m[1]!, href: m[2]! });
    last = idx + m[0].length;
  }
  if (last < input.length) parts.push({ text: input.slice(last) });
  return parts.length ? parts : [{ text: input }];
}

/** Blog listesi satırı (herkese açık; K-172) */
export interface BlogPostSummary {
  slug: string;
  locale: string;
  title: string;
  excerpt: string | null;
  publishedAt: string;
}
export interface PublicBlogList {
  posts: BlogPostSummary[];
}
